import { Component, Inject, input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { ProductionPriceListApiService } from 'src/api/services/production-price-list-api.service';
import { ImportedPriceListVariantDetail, ImportPriceListExcel } from 'src/state/production-price-list/state-models/production-price-list';
import stringify from 'fast-json-stable-stringify';
import _, { last } from 'lodash';
import toastConfig from 'src/app/shared/custom-toast-options';
import { ToastrService } from 'ngx-toastr';
import { CommonStateService } from 'src/state/common/service';
import { ProductionPriceListStateService } from 'src/state/production-price-list/service';

@Component({
    selector: 'cal-price-list-excel-dialogbox',
    templateUrl: './import-price-list-excel-dialogbox.component.html',
    styleUrl: './import-price-list-excel-dialogbox.component.css',
    standalone: false
})
export class ImportPriceListExcelDialogboxComponent {
  public resources: any = this.commonService.getResources();
  previewHeaders: string[] = [];
  validatedData: any[] = [];
  dateHeaders = ['ValidFrom', 'ValidTo'];
  readonly excludedKeys: string[] = ['FamilyDescription', 'OptionDescription', ''];
  isSaveEnabled = false;
  priceListId: number | null;

  constructor(
    public dialogRef: MatDialogRef<ImportPriceListExcelDialogboxComponent>,
    private importPriceListExcelApiService: ProductionPriceListApiService,
    private importPriceListExcelStateService: ProductionPriceListStateService,
    private toastr: ToastrService,
    private commonService: CommonStateService,
    @Inject(MAT_DIALOG_DATA) public inputData: {
      selectedValue: number | null,
      tableData: any[] | null
    }) {
    this.priceListId = inputData.selectedValue;
    const data = inputData.tableData || [];
    if (data && Array.isArray(data) && data.length > 0) {
      this.validatedData = this.validateAllImportedExcelFields(data);

      // Extract all keys dynamically from the dataset
      const allKeysSet = new Set<string>();
      data.forEach(obj => {
        Object.keys(obj).forEach(key => allKeysSet.add(key));
      });

      const allKeys = Array.from(allKeysSet);

      // Filter out unwanted keys
      this.previewHeaders = allKeys.filter(
        key => !this.excludedKeys.includes(key.trim())
      );

      // 'ValidationStatus' appear FIRST
      this.previewHeaders = this.previewHeaders.filter(key => key !== 'ValidationStatus');
      this.previewHeaders.unshift('ValidationStatus');
    }
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isValidDate(value: string): boolean {
    return moment(value, 'YYYY-MM-DD', true).isValid();
  }

  validateAllImportedExcelFields(data: any[]): any[] {
    const seenRowsWithoutCondition = new Set<string>();
    const seenConditionIds = new Set<string>();
    const validIntegerRegex = /^\d+$/;
    const validOptionRegex = /^[A-Z]+$/;

    return data.map((row, index) => {
      const errorsInExcel: string[] = [];

      const conditionId = row['Condition'];
      let price = row['Price'];
      const componentType = row['ComponentType'];
      const conditionDescription = row['ConditionDescription'];
      const priceType = row['PriceType'];
      const validFrom = row['ValidFrom'];
      const validTo = row['ValidTo'];
      const market = row['Market'];

      // === Condition ===
      const isConditionEmpty = conditionId === null || conditionId === undefined || conditionId.toString().trim() === '';
      if (isConditionEmpty) {
        // For null/empty Condition, check duplicates based on variant combinations only
        const variantKey = this.compareVariantsOnly(row);
        if (seenRowsWithoutCondition.has(variantKey)) {
          errorsInExcel.push(`Duplicate row found: same variant combination already exists in other row.`);
        } else {
          seenRowsWithoutCondition.add(variantKey);
        }
      } else if (!validIntegerRegex.test(conditionId.toString().trim())) {
        errorsInExcel.push(`Invalid Condition Id (${conditionId}). Must be a positive integer.`);
      } else {
        // For valid Condition, check duplicates only by Condition Id (ignore row content)
        if (seenConditionIds.has(conditionId)) {
          errorsInExcel.push(`Duplicate Condition Id found (ID: ${conditionId}).`);
        } else {
          seenConditionIds.add(conditionId);
        }
      }

      // === Price validation ===
      if (typeof price === 'string') {
        price = price.replace(/\s/g, '').replace(',', '.');
      }

      const refactoredPrice = Number(price);
      if (isNaN(refactoredPrice)) {
        errorsInExcel.push(`Invalid Price value (${row['Price']}). Must be a number.`)
      } else {
        row['Price'] = Math.round(refactoredPrice * 100) / 100;
      }

      // === Component Type ===
      if (!componentType || componentType.toString().trim() === '') {
        errorsInExcel.push(`'Component Type' cannot be null or empty.`)
      }

      // === Condition Description ===
      if (!conditionDescription || conditionDescription.toString().trim() === '') {
        errorsInExcel.push(`'Condition Description' cannot be null or empty.`);
      } else if (conditionDescription.toString().trim().length > 80) {
        errorsInExcel.push(`'Condition Description' is too long.`);
      }

      // === Price Type ===
      if (!priceType || priceType.toString().trim() === '') {
        errorsInExcel.push(`'Price Type' cannot be null or empty.`)
      }

      // === Market Check ===
      if (!market || market.toString().trim() === '') {
        errorsInExcel.push(`'Market' cannot be null or empty.`)
      }

      // === Variant Family and Variant Option check ===
      const variantFamilyKeys = Object.keys(row).filter(key => key.startsWith('Variant.Family'));
      const variantOptionKeys = Object.keys(row).filter(key => key.startsWith('Variant.Option'));

      const seenVariantPairs = new Set(); // Added to check duplicate VF, VO

      variantFamilyKeys.forEach(familyKey => {
        const val = row[familyKey];
        const index = familyKey.match(/\d+$/)?.[0];
        if (!index) return;

        const optionKey = `Variant.Option ${index}`;
        let optionVal = row[optionKey];

        const isVFEmpty = val === undefined || val === null || val.toString().trim() === '';
        const isVOEmpty = optionVal === undefined || optionVal === null || optionVal.toString().trim() === '';

        // === Skip validation if BOTH VF and VO are empty ===
        if (isVFEmpty && isVOEmpty) {
          row[optionKey] = null;
          return;
        }

        // === Validate Variant.Family ===
        if (!isVFEmpty) {
          const strVal = val.toString().trim();
          if (!validIntegerRegex.test(strVal)) {
            errorsInExcel.push(`'${familyKey}' must be a whole number. Invalid value: ${val}`);
          }
        } else {
          errorsInExcel.push(`'${familyKey}' is required and cannot be empty.`);
        }

        // === Validate Variant.Option ===
        if (optionVal === '*') {
          row[optionKey] = null;
        } else if (!isVOEmpty) {
          const strOptionVal = optionVal.toString().trim();
          if (!validOptionRegex.test(strOptionVal)) {
            errorsInExcel.push(`'${optionKey}' must contain only capital letters (A-Z). Invalid value: ${optionVal}`);
          }
        } else {
          errorsInExcel.push(`'${optionKey}' is required and cannot be empty.`);
        }

        // === Duplicate check ===
        const strVF = val?.toString().trim() ?? '';
        const strVO = optionVal?.toString().trim() ?? '';

        if (
          strVF && strVO &&
          validIntegerRegex.test(strVF) &&
          validOptionRegex.test(strVO)
        ) {
          const pair = `${strVF}-${strVO}`;
          if (seenVariantPairs.has(pair)) {
            errorsInExcel.push(`Duplicate Variant.Family and Option combination found in this row: '${pair}'`);
          } else {
            seenVariantPairs.add(pair);
          }
        }
      });

      // === ValidFrom and ValidTo ===
      const today = moment().utc();

      if (!validFrom || !this.isValidDate(validFrom)) {
        errorsInExcel.push(`'ValidFrom' is required and must be a valid date.`);
      } else {
        const validFromDate = moment(new Date(validFrom));
        if (!validFromDate.isValid()) {
          errorsInExcel.push(`'ValidFrom' (${validFrom}) is not a valid date.`);
        // } else if (!validFromDate.isAfter(today)) {
        //   errorsInExcel.push(`'ValidFrom' (${validFromDate.format('YYYY-MM-DD')}) must be greater than current date.`);
        }

        if (validTo) {
          const validToDate = moment(new Date(validTo));
          if (!validToDate.isValid()) {
            errorsInExcel.push(`'ValidTo' (${validTo}) must be a valid date or blank.`);
          } else if (validToDate.isBefore(validFromDate)) {
            errorsInExcel.push(`'ValidTo' (${validToDate.format('YYYY-MM-DD')}) cannot be before 'ValidFrom' (${validFromDate.format('YYYY-MM-DD')}).`);
          } else {
            row['ValidTo'] = validToDate.format('YYYY-MM-DD');
          }
        }

        // Normalize date formats
        row['ValidFrom'] = validFromDate.format('YYYY-MM-DD');
      }

      row['ValidationStatus'] = errorsInExcel;

      return row;
    });
  }

  compareEachRowData(row: any): string {
    // Exclude Condition and ValidationStatus from the object
    const obj = _.omit(row, ['Condition', 'ValidationStatus']);
    return stringify(obj);
  }

  compareVariantsOnly(row: any): string {
    // Extract only Variant.Family and Variant.Option fields for comparison
    const variantData: Record<string, any> = {};
    Object.keys(row).forEach(key => {
      if (key.startsWith('Variant.Family') || key.startsWith('Variant.Option')) {
        variantData[key] = row[key];
      }
    });
    return stringify(variantData);
  }

  hasNoErrors() {
    if (!this.validatedData?.length)
      return false;
    return this.validatedData.filter(ob => {
      return ob.ValidationStatus.length > 0;
    }).length == 0;
  }

  isValidating = false;
  isSaving = false;

  async validate() {
    if (!this.hasNoErrors())
      return;
    this.isValidating = true;
    const payload: ImportPriceListExcel[] = this.validatedData.map(obj => {
      return {
        priceListId: this.priceListId,
        componentType: obj.ComponentType,
        condition: Number(obj.Condition),
        conditionDescription: obj.ConditionDescription,
        importedPriceListVariantDetails: this.updateAndValidateVariantDetails(obj),
        price: obj.Price,
        priceType: obj.PriceType,
        validFrom: obj.ValidFrom,
        validTo: obj.ValidTo,
        market: obj.Market,
        errorMessage: obj.ValidationStatus
      }
    })

    const result = await this.importPriceListExcelApiService.verify(payload);
    const initialData = this.validatedData.slice();
    result.data.productionPriceListImport.verify.forEach((ob, idx) => {
      initialData[idx].ValidationStatus = ob.errorMessage;
    })
    this.validatedData = initialData.slice();
    if (!this.hasNoErrors()) {
      this.toastr.warning(this.resources.ImportedPriceListDataValidationError, this.resources.Info, toastConfig);
    } else {
      this.toastr.success(this.resources.ImportedPriceListDataValidatedSuccessfully, this.resources.Success, toastConfig);
    }
    this.isValidating = false;
    this.isSaveEnabled = this.hasNoErrors();
  }

  async save() {
    if (!this.hasNoErrors())
      return;
    this.isSaving = true;
    const payload: ImportPriceListExcel[] = this.validatedData.map(obj => {
      return {
        priceListId: this.priceListId,
        componentType: obj.ComponentType,
        condition: Number(obj.Condition),
        conditionDescription: obj.ConditionDescription,
        importedPriceListVariantDetails: this.updateAndValidateVariantDetails(obj),
        price: obj.Price,
        priceType: obj.PriceType,
        validFrom: obj.ValidFrom,
        validTo: obj.ValidTo,
        market: obj.Market,
        errorMessage: obj.ValidationStatus
      }
    })
    const result = await this.importPriceListExcelApiService.confirm(payload);
    if (result.data.productionPriceListImport.confirm) {
      this.toastr.success(this.resources.ImportedPriceListDataSavedSuccessfully, this.resources.Success, toastConfig)
      this.isSaving = false;
      this.closeModel();
      this.importPriceListExcelStateService.refetchList(this.priceListId);
    } else {
      this.toastr.error(this.resources.ImportedPriceListDataSavingFailed, this.resources.Error, toastConfig);
      this.isSaveEnabled = false;
    }
    this.isSaving = false;
  }

  updateAndValidateVariantDetails(obj: any): ImportedPriceListVariantDetail[] {
    return Object.keys(obj)
      .filter(key => key.startsWith('Variant.Family'))
      .map(familyKey => {
        const index = familyKey.split(' ')[1];
        const initialVariantFamily = obj[familyKey];
        const variantOption = obj[`Variant.Option ${index}`];

        const variantFamily = initialVariantFamily;

        return variantFamily ? { variantClass: "FPC", variantFamily, variantOption } : null;
      }).filter(Boolean);
  }

  closeModel() {
    this.dialogRef.close()
  }
}